// Owner: shell-nav (plan §3.1, §9.2). The web side of the navigation model: which rail item a URL
// activates and the library path. The settings pages are account-settings' model
// (`features/account/settings-nav.ts`), which the settings rail, the settings index, ⌘K and the phone
// Menu all read.

/**
 * Active item of the rail and the mobile tab bar for a pathname: `home`, `inbox`, `meetings`, `engagements`, `library`,
 * `object:<key>`, `app:<key>` (a custom component opened as an app), `components` (the list of custom components),
 * `data-quality`, `automations`, `settings` (Settings and the Account pages, which share the settings layout; null
 * elsewhere).
 */
export function appActiveKey(pathname: string): string | null {
  const p = pathname.replace(/\/+$/, '') || '/';
  if (p === '/') return 'home';
  const under = (prefix: string) => p === prefix || p.startsWith(`${prefix}/`);
  if (under('/inbox') || under('/tasks') || under('/signatures') || under('/change-sets')) return 'inbox';
  if (under('/meetings')) return 'meetings';
  if (under('/engagements')) return 'engagements';
  // A record's document variant and the variants of a template are reached from a record or an object, not
  // from the library: no rail item claims them.
  if (under('/documents/variant') || under('/documents/variants')) return null;
  // The library holds pages, presentations, document templates and dashboards (plan §3.3).
  if (under('/library') || under('/pages') || under('/dashboards') || under('/decks') || under('/documents'))
    return 'library';
  if (p === '/apps') return 'components';
  if (under('/data-quality')) return 'data-quality';
  const comp = /^\/apps\/([^/]+)/.exec(p);
  if (comp) return `app:${decodeURIComponent(comp[1]!)}`;
  const obj = /^\/o\/([^/]+)/.exec(p);
  if (obj) return `object:${decodeURIComponent(obj[1]!)}`;
  if (under('/automations')) return 'automations';
  if (under('/settings') || under('/account')) return 'settings';
  return null;
}

/** The library's path: `/library` once the library ships it, `/pages` until then. */
/** (A plain string: the `/library` route is not in the route tree until the library ships it.) */
export function libraryPath(hasRoute: (path: string) => boolean): string {
  return hasRoute('/library') ? '/library' : '/pages';
}
